import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale, ParticipantFieldScope, ParticipantFieldType } from '__generated__/graphql';
import { Translation, translationsForAttribute } from '../../../Translations/helpers';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeleteParticipantFieldMutation from './DeleteParticipantFieldMutation';
import EditParticipantFieldMutation from './EditParticipantFieldMutation';
import ParticipantFieldForm from './ParticipantFieldForm';
import UI from '../../../../common/UI';
import getParticipantFieldSchema from './participantFieldSchema';
import useMutation from '../../../../api/useMutation';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

interface Event {
  id: string;
  title: string;
  locale: Locale;
}

interface ParticipantFieldChoice {
  id: string;
  title: string;
  enabled: boolean;
  deletable: boolean;
  translations: Translation[];
}

interface Sellable {
  id: string;
  title: string;
}

interface Promotion {
  id: string;
  title?: string | null;
}

export interface ParticipantField {
  id: string;
  title: string;
  description?: string | null;
  type: ParticipantFieldType;
  scope: ParticipantFieldScope;
  choices: ParticipantFieldChoice[];
  tickets: Sellable[];
  products: Sellable[];
  promotions: Promotion[];
  required: boolean;
  unique: boolean;
  validation_url?: string | null;
  validation_success_response?: string | null;
  field_entry_count: number;
  config?: string | null;
  translations: Translation[];
  deletable: boolean;
}

const getValues = (field: ParticipantField) => ({
  id: field.id,
  title: field.title,
  title_translations: translationsForAttribute(field.translations, 'title'),
  description: field.description,
  description_translations: translationsForAttribute(field.translations, 'description'),
  choices: field.choices.map(({ id, title, enabled, translations }) => ({
    id,
    title,
    title_translations: translationsForAttribute(translations, 'title'),
    enabled,
  })),
  tickets: field.tickets.map(({ id }) => ({ id })),
  products: field.products.map(({ id }) => ({ id })),
  promotions: field.promotions.map(({ id }) => ({ id })),
  required: field.required,
  unique: field.unique,
  validation_url: field.validation_url,
  validation_success_response: field.validation_success_response,
  config: field.config ? JSON.parse(field.config) : null,
});

export interface EditParticipantFieldFormProps {
  event: Event;
  field: ParticipantField;
  closeTo?: string;
  onDelete?: () => void;
  refetchQueries?: DocumentNode[];
}

const EditParticipantFieldForm = ({
  event, field, closeTo, onDelete, refetchQueries,
}: EditParticipantFieldFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: getValues(field),
  });

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editParticipantField, { loading, error }] = useMutation(
    EditParticipantFieldMutation,
    {
      variables: {
        input: {
          ...form.values,
          config: form.values.config ? JSON.stringify(form.values.config) : null,
        },
      },
      refetchQueries,
      onCompleted: ({ editParticipantField }) => {
        notifier.success(t('notifications.field_saved'));
        form.reset(getValues(editParticipantField));
        setFormKey((key) => key + 1);
      },
      onError: () => {
        scrollTo(`EditParticipantFieldForm_${field.id}`, { gutter: 1 });
      },
    },
  );

  const [deleteParticipantField] = useMutation(
    DeleteParticipantFieldMutation, {
      variables: {
        id: field.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onDelete?.();
        notifier.success(t('notifications.field_deleted'));
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: getParticipantFieldSchema(field.type),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editParticipantField()} id={`EditParticipantFieldForm_${field.id}`} key={formKey}>
      <UI.ServerErrors error={error} sc={{ mb: 4 }} />
      <ParticipantFieldForm form={form} field={field} event={event} errors={errors} />
      <UI.HR />
      <UI.FormButtons
        form={form}
        submitLabel={t('save_field')}
        closeTo={closeTo}
        deleteLabel={t('delete_field')}
        onDelete={field.deletable ? deleteParticipantField : null}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default EditParticipantFieldForm;
